import {
  DestroyRef,
  Directive,
  ElementRef,
  NgZone,
  afterNextRender,
  inject,
} from '@angular/core';

/** Gap kept between the panel and the viewport edges. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * Height a shifted panel can occupy, published so panels that scroll
 * internally can grow with the viewport instead of a hardcoded cap.
 */
const AVAILABLE_HEIGHT_PROPERTY = '--keep-in-viewport-available-height';

/**
 * Keeps an absolutely positioned filter panel inside the viewport.
 *
 * Filter chips anchor their panels to the chip (`left: 0` for most, `right: 0`
 * for the "More Filters" menu), so a chip near a viewport edge pushes its panel
 * off-screen. This shifts the panel on top of any transform the stylesheet
 * already applies, without changing its anchor or width.
 */
@Directive({
  selector: '[appKeepInViewport]',
  standalone: true,
})
export class KeepInViewportDirective {
  private readonly host =
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);
  private readonly reposition = () => this.applyViewportShift();
  private resizeObserver?: ResizeObserver;

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterNextRender(() => {
      this.zone.runOutsideAngular(() => {
        this.applyViewportShift();
        window.addEventListener('resize', this.reposition, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(this.reposition);
          this.resizeObserver.observe(this.host);
        }
      });

      destroyRef.onDestroy(() => {
        window.removeEventListener('resize', this.reposition);
        this.resizeObserver?.disconnect();
      });
    });
  }

  private applyViewportShift(): void {
    const element = this.host;
    element.style.transform = '';
    element.style.setProperty(
      AVAILABLE_HEIGHT_PROPERTY,
      `${Math.max(0, window.innerHeight - 2 * VIEWPORT_MARGIN_PX)}px`
    );

    const stylesheetTransform = getComputedStyle(element).transform;
    const baseTransform =
      stylesheetTransform === 'none' ? '' : stylesheetTransform;

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return;
    }

    let shiftX = 0;
    let shiftY = 0;

    if (rect.width > 0) {
      const overflowRight =
        rect.right - (window.innerWidth - VIEWPORT_MARGIN_PX);
      if (overflowRight > 0) {
        shiftX = -overflowRight;
      }

      const overflowLeft = VIEWPORT_MARGIN_PX - (rect.left + shiftX);
      if (overflowLeft > 0) {
        shiftX += overflowLeft;
      }
    }

    if (rect.height > 0) {
      const overflowTop = VIEWPORT_MARGIN_PX - rect.top;
      if (overflowTop > 0) {
        shiftY = overflowTop;
      } else {
        const overflowBottom =
          rect.bottom - (window.innerHeight - VIEWPORT_MARGIN_PX);
        if (overflowBottom > 0) {
          shiftY = -overflowBottom;
          // Prefer keeping the top edge visible when the panel is taller than
          // the viewport; scrolling inside the panel handles the rest.
          if (rect.top + shiftY < VIEWPORT_MARGIN_PX) {
            shiftY = VIEWPORT_MARGIN_PX - rect.top;
          }
        }
      }
    }

    const parts: string[] = [];
    if (Math.abs(shiftX) >= 1) {
      parts.push(`translateX(${Math.round(shiftX)}px)`);
    }
    if (Math.abs(shiftY) >= 1) {
      parts.push(`translateY(${Math.round(shiftY)}px)`);
    }
    if (baseTransform) {
      parts.push(baseTransform);
    }

    element.style.transform = parts.join(' ');
  }
}
